import { format } from 'pretty-format'

import { UserFriendlyError } from './user-friendly-error'

/**
 * Enables type-checking that is not affected by having multiple versions of
 * this library installed
 */
// The string here is used as the type description for the type checker
const MARKER_SYMBOL = Symbol.for('@attain-apps/ValidationError')

/**
 * REVIEW: revisit documentation, because it is sometimes misleading.
 *
 * Some item was requested, but that item does not appear to exist
 *
 * @example
 * if (!userId.startsWith(USER_ID_PREFIX)) {
 *   throw new ValidationError({
 *     reason: `User IDs must start with '${USER_ID_PREFIX}'`,
 *     type: 'user ID',
 *     value: userId,
 *   })
 * }
 */
export class ValidationError extends UserFriendlyError {
  // NOTE: must be all upper case to avoid naming collision with the class name
  public static override readonly NAME: string = 'ValidationError'
  public override readonly name: string = ValidationError.NAME

  // Used by TypeChecker
  private readonly [MARKER_SYMBOL] = true as const

  constructor(
    { reason, type = 'item', value }: ValidationError.constructor.Options = {},
    actualError?: Error,
  ) {
    super(
      `Invalid ${type}${value !== undefined ? `: ${format(value)}` : ''}${
        reason !== undefined ? `. ${reason}` : ''
      }`,
      actualError,
    )
  }
}
export namespace ValidationError {
  export namespace constructor {
    export interface Options {
      /**
       * Describes what type of thing is invalid.
       *
       * @default 'value'
       */
      type?: string
      /**
       * The invalid value
       *
       * @default not specified
       */
      value?: unknown
      /**
       * Why the thing is invalid. Should be sentence-case
       *
       * @default not specified
       */
      reason?: string
    }
  }

  // NOTE: not using @attain-apps/type-guards as that would introduce circular imports
  export function isValidationError(val: unknown): val is ValidationError {
    return !!(val as ValidationError)?.[MARKER_SYMBOL]
  }
}
